import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  ArrowLeft,
  Loader2,
  Package,
  CheckCircle,
  XCircle,
  Calendar,
  Factory,
  Warehouse,
  FileText,
  ExternalLink,
} from 'lucide-react';
import Layout from '../components/Layout';
import subcontractService from '../services/subcontractService';
import SubcontractIssueDialog from '../components/subcontract/SubcontractIssueDialog';
import SubcontractCompleteDialog from '../components/subcontract/SubcontractCompleteDialog';

function SubcontractDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState(null);
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);

  const loadOrder = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const orderData = await subcontractService.getSubcontractOrder(id);
      setOrder(orderData.data || orderData);
    } catch (err) {
      console.error('Failed to load subcontract order:', err);
      setError('加载外协单失败');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const handleBack = () => navigate('/subcontract');

  const handleIssue = () => {
    setIssueDialogOpen(true);
  };

  const handleStart = async () => {
    if (!window.confirm('确认标记外协单为加工中？')) return;
    try {
      setActionLoading(true);
      await subcontractService.markInProgress(id);
      loadOrder();
    } catch (err) {
      setError(err.message || '操作失败');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReceive = () => {
    setCompleteDialogOpen(true);
  };

  const handleCompleteSuccess = () => {
    setCompleteDialogOpen(false);
    loadOrder();
  };

  const handleCancel = async () => {
    if (!window.confirm('确定要取消此外协单吗？')) return;
    try {
      setActionLoading(true);
      await subcontractService.cancelOrder(id);
      loadOrder();
    } catch (err) {
      setError(err.message || '操作失败');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: { variant: 'secondary', label: '草稿' },
      issued: { variant: 'warning', label: '已发料' },
      in_progress: { variant: 'default', label: '加工中' },
      completed: { variant: 'success', label: '已完成' },
      cancelled: { variant: 'destructive', label: '已取消' },
    };
    const { variant, label } = config[status] || { variant: 'secondary', label: status };
    return <Badge variant={variant}>{label}</Badge>;
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto" />
          <p className="text-muted-foreground mt-4">加载中...</p>
        </div>
      </Layout>
    );
  }

  if (!order) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <Alert variant="destructive">
            <AlertDescription>外协单不存在或已被删除</AlertDescription>
          </Alert>
          <Button className="mt-4" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            返回列表
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">{order.subcontract_order_id}</h1>
              <p className="text-sm text-muted-foreground">
                工序: {order.step_name} | 供应商: {order.supplier_name}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {getStatusBadge(order.status)}
            {order.settlement_status === 'settled' && (
              <Badge variant="success">已结算</Badge>
            )}
          </div>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 mb-6">
          {order.status === 'draft' && (
            <Button onClick={handleIssue} disabled={actionLoading} className="bg-amber-600 hover:bg-amber-700">
              <Package className="h-4 w-4 mr-2" />
              发料出库
            </Button>
          )}
          {order.status === 'issued' && (
            <Button onClick={handleStart} disabled={actionLoading}>
              <Factory className="h-4 w-4 mr-2" />
              标记加工中
            </Button>
          )}
          {order.status === 'in_progress' && (
            <Button onClick={handleReceive} disabled={actionLoading} className="bg-green-600 hover:bg-green-700">
              <CheckCircle className="h-4 w-4 mr-2" />
              确认完成
            </Button>
          )}
          {['draft', 'issued', 'in_progress'].includes(order.status) && (
            <Button variant="destructive" onClick={handleCancel} disabled={actionLoading}>
              <XCircle className="h-4 w-4 mr-2" />
              取消
            </Button>
          )}
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">供应商</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-lg font-semibold">{order.supplier_name}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">计划数量</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-lg font-semibold">
                {order.total_completed_quantity > 0
                  ? `${order.total_completed_quantity} / ${order.total_planned_quantity}`
                  : order.total_planned_quantity}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">金额</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-lg font-semibold">
                {order.settlement_amount > 0
                  ? `${order.settlement_amount.toFixed(2)} 元`
                  : `${order.total_amount?.toFixed(2)} 元`}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Items Table */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Factory className="h-5 w-5" />
              关联工单
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>工单号</TableHead>
                  <TableHead>产品编码</TableHead>
                  <TableHead className="text-center">计划数量</TableHead>
                  <TableHead className="text-center">完成数量</TableHead>
                  <TableHead className="text-center">报废数量</TableHead>
                  <TableHead className="text-center">状态</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {order.items?.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-medium">
                      <Button
                        variant="link"
                        className="p-0 h-auto"
                        onClick={() => navigate(`/production/${item.production_order_id}`)}
                      >
                        {item.production_order_number || item.production_order_id}
                      </Button>
                    </TableCell>
                    <TableCell>{item.finished_product_code}</TableCell>
                    <TableCell className="text-center">{item.planned_quantity}</TableCell>
                    <TableCell className="text-center">{item.completed_quantity}</TableCell>
                    <TableCell className="text-center">{item.scrap_quantity}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant={item.status === 'completed' ? 'success' : 'secondary'}>
                        {item.status === 'completed' ? '已完成' : '待完成'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Material Requisitions */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              领料申请单
            </CardTitle>
          </CardHeader>
          <CardContent>
            {order.material_requisitions && order.material_requisitions.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>申请单号</TableHead>
                    <TableHead className="text-center">状态</TableHead>
                    <TableHead className="text-center">创建时间</TableHead>
                    <TableHead className="text-center">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {order.material_requisitions.map((req, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">
                        <Button
                          variant="link"
                          className="p-0 h-auto"
                          onClick={() => navigate(`/material-requisitions/${req.requisition_id}`)}
                        >
                          {req.requisition_id}
                        </Button>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge
                          variant={
                            req.status === 'approved'
                              ? 'success'
                              : req.status === 'rejected'
                              ? 'destructive'
                              : req.status === 'cancelled'
                              ? 'secondary'
                              : 'warning'
                          }
                        >
                          {req.status === 'pending_approval'
                            ? '待审批'
                            : req.status === 'approved'
                            ? '已审批'
                            : req.status === 'rejected'
                            ? '已拒绝'
                            : req.status === 'cancelled'
                            ? '已取消'
                            : req.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {req.created_at
                          ? new Date(req.created_at).toLocaleDateString('zh-CN')
                          : '-'}
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/material-requisitions/${req.requisition_id}`)}
                        >
                          <ExternalLink className="h-4 w-4 mr-1" />
                          查看
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-sm text-muted-foreground">暂无领料申请单</p>
            )}
          </CardContent>
        </Card>

        {/* Warehouse Movements */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Warehouse className="h-5 w-5" />
              仓库记录
            </CardTitle>
          </CardHeader>
          <CardContent>
            {order.material_issue_movement ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Package className="h-4 w-4 text-red-500" />
                  <span className="font-medium">发料出库:</span>
                  <span className="font-mono">{order.material_issue_movement.movement_id}</span>
                  <Badge variant="outline">{order.material_issue_movement.total_quantity} 件</Badge>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">暂无发料记录</p>
            )}
            {order.material_return_movement && (
              <div className="space-y-2 mt-2">
                <div className="flex items-center gap-2 text-sm">
                  <Package className="h-4 w-4 text-green-500" />
                  <span className="font-medium">余料退回:</span>
                  <span className="font-mono">{order.material_return_movement.movement_id}</span>
                  <Badge variant="outline">{order.material_return_movement.total_quantity} 件</Badge>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dates & Notes */}
        <Card>
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">计划交期:</span>
              <span>{order.expected_return_date || '未设置'}</span>
            </div>
            {order.actual_return_date && (
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">实际收回:</span>
                <span>{new Date(order.actual_return_date).toLocaleDateString('zh-CN')}</span>
              </div>
            )}
            {order.notes && (
              <div className="text-sm">
                <span className="text-muted-foreground">备注:</span> {order.notes}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <SubcontractIssueDialog
        open={issueDialogOpen}
        onOpenChange={setIssueDialogOpen}
        order={order}
        onSuccess={() => {
          setIssueDialogOpen(false);
          loadOrder();
        }}
      />

      <SubcontractCompleteDialog
        open={completeDialogOpen}
        onClose={() => setCompleteDialogOpen(false)}
        order={order}
        onSuccess={handleCompleteSuccess}
      />
    </Layout>
  );
}

export default SubcontractDetailPage;
